import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
  IconButton,
  InputAdornment,
  Popover,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon, ClearAll as ClearIcon, Clear as ClearIconSmall, ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, UnfoldMore as UnsortedIcon } from '@mui/icons-material';
import { baseUrls } from '../../../constants/module-constants';
import { useAuthContext } from '../../../context/auth-context';
import { authFetch } from '../../../lib/http';

const COLUMNS = [
  { key: 'eventName',       label: 'Evento' },
  { key: 'affiliationName', label: 'Asociación/Misión' },
  { key: 'affiliationZone', label: 'Zona' },
  { key: 'clubName',        label: 'Club' },
  { key: 'rubric',          label: 'Rúbrica' },
  { key: 'maxGrade',        label: 'Puntos Máximos', align: 'center' },
  { key: 'grade',           label: 'Calificación',   align: 'center' },
  { key: 'comments',        label: 'Comentarios' },
  { key: 'savedBy',         label: 'Evaluado Por' },
];

const DEFAULT_COL_WIDTHS = {
  eventName:       150,
  affiliationName: 160,
  affiliationZone: 110,
  clubName:        150,
  rubric:          210,
  maxGrade:         95,
  grade:            95,
  comments:        240,
  savedBy:         150,
};

const RubricsGradesDetailsPage = () => {
  const { activeCamp } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [allGrades, setAllGrades] = useState([]);
  const [filters, setFilters] = useState({ event: '', zone: '', club: '', savedBy: '', comments: '' });
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS);
  const [visibleCols, setVisibleCols] = useState(() => Object.fromEntries(COLUMNS.map((c) => [c.key, true])));
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const [displayCount, setDisplayCount] = useState(50);
  const resizingRef = useRef(null);
  const sentinelRef = useRef(null);

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => visibleCols[c.key]), [visibleCols]);

  const toggleCol = useCallback((key) => {
    setVisibleCols((prev) => {
      const nextVisible = !prev[key];
      const wouldHaveNone = Object.entries({ ...prev, [key]: nextVisible }).every(([, v]) => !v);
      if (wouldHaveNone) return prev;
      return { ...prev, [key]: nextVisible };
    });
  }, []);

  const dropdownOptions = useMemo(() => {
    const events   = [...new Set(allGrades.map((g) => g.eventName))].sort();
    const zones    = [...new Set(allGrades.map((g) => g.affiliationZone))].sort();
    const clubs    = [...new Set(allGrades.map((g) => g.clubName))].sort((a, b) => a.localeCompare(b));
    const savedBys = [...new Set(allGrades.map((g) => g.savedBy))].sort();
    return { events, zones, clubs, savedBys };
  }, [allGrades]);

  const filteredGrades = useMemo(() => {
    let result = allGrades.filter((grade) =>
      (!filters.event    || grade.eventName === filters.event) &&
      (!filters.zone     || grade.affiliationZone === filters.zone) &&
      (!filters.club     || grade.clubName === filters.club) &&
      (!filters.savedBy  || grade.savedBy === filters.savedBy) &&
      (!filters.comments ||
        (filters.comments === 'withValue'    && grade.comments) ||
        (filters.comments === 'withoutValue' && !grade.comments))
    );

    if (sort.key) {
      result = [...result].sort((a, b) => {
        const aVal = a[sort.key] ?? '';
        const bVal = b[sort.key] ?? '';
        const cmp  = typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
        return sort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [allGrades, filters, sort]);

  useEffect(() => {
    if (!activeCamp?._id) { setAllGrades([]); return; }
    fetchGrades(activeCamp._id);
  }, [activeCamp?._id]);

  useEffect(() => {
    setDisplayCount(50);
  }, [filters, sort]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDisplayCount((prev) => prev + 50);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredGrades]);

  const fetchGrades = async (campId) => {
    setLoading(true);
    try {
      const response = await authFetch(`${baseUrls.baseUrl}/rubric-grade/get-rubric-grade-by-campid/${campId}`);
      const data = await response.json();
      setAllGrades(data.results);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ event: '', zone: '', club: '', savedBy: '', comments: '' });
  }, []);

  const handleSortClick = useCallback((key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX     = e.clientX;
    const startWidth = colWidths[colKey];
    resizingRef.current = { colKey, startX, startWidth };

    const onMouseMove = (ev) => {
      if (!resizingRef.current) return;
      const { colKey: k, startX: sx, startWidth: sw } = resizingRef.current;
      setColWidths(prev => ({ ...prev, [k]: Math.max(60, sw + (ev.clientX - sx)) }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  const exportToCsv = useCallback(() => {
    const headers = ['Event', 'Asociación/Misión', 'Zona', 'Club', 'Rubric', 'MaxGrade', 'Grade', 'Comments', 'Saved By'];
    const csvRows = [headers.join(',')];
    filteredGrades.forEach((grade) => {
      const row = [
        grade.eventName, grade.affiliationName, grade.affiliationZone,
        grade.clubName,  grade.rubric,          grade.maxGrade,
        grade.grade,     grade.comments || '',   grade.savedBy,
      ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`);
      csvRows.push(row.join(','));
    });
    const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'rubrics_grades_details.csv';
    link.click();
  }, [filteredGrades]);

  const SortIcon = ({ colKey }) => {
    if (sort.key !== colKey) return <UnsortedIcon sx={{ fontSize: 14, opacity: 0.3, ml: 0.5, verticalAlign: 'middle', flexShrink: 0 }} />;
    return sort.direction === 'asc'
      ? <ArrowUpIcon   sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'primary.main', flexShrink: 0 }} />
      : <ArrowDownIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'primary.main', flexShrink: 0 }} />;
  };

  const totalWidth = visibleColumns.reduce((sum, col) => sum + colWidths[col.key], 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Detalles de Calificaciones por Rúbricas</Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" color="primary" startIcon={<ClearIcon />} onClick={clearFilters}>
            Limpiar Filtros
          </Button>
          <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={(e) => setColMenuAnchor(e.currentTarget)}>
            Columnas
          </Button>
          <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={exportToCsv}>
            Exportar CSV
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { field: 'event',    label: 'Evento',        options: dropdownOptions.events },
          { field: 'zone',     label: 'Zona',          options: dropdownOptions.zones },
          { field: 'club',     label: 'Club',          options: dropdownOptions.clubs },
          { field: 'savedBy',  label: 'Evaluado Por',  options: dropdownOptions.savedBys },
        ].map(({ field, label, options }) => (
          <Grid item xs={12} sm={6} md={2.4} key={field}>
            <FormControl fullWidth>
              <InputLabel>{label}</InputLabel>
              <Select
                value={filters[field]}
                label={label}
                onChange={(e) => handleFilterChange(field, e.target.value)}
                endAdornment={
                  filters[field] && (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <IconButton size="small" onClick={() => handleFilterChange(field, '')} edge="end">
                        <ClearIconSmall fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        ))}

        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth>
            <InputLabel>Comentarios</InputLabel>
            <Select
              value={filters.comments}
              label="Comentarios"
              onChange={(e) => handleFilterChange('comments', e.target.value)}
              endAdornment={
                filters.comments && (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <IconButton size="small" onClick={() => handleFilterChange('comments', '')} edge="end">
                      <ClearIconSmall fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="withValue">Con Comentarios</MenuItem>
              <MenuItem value="withoutValue">Sin Comentarios</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 280px)',
          }}
        >
          {/* Table with fixed total width so horizontal scroll works */}
          <Box sx={{ minWidth: totalWidth }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #ddd',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                userSelect: 'none',
              }}
            >
              {visibleColumns.map((col) => (
                <Box
                  key={col.key}
                  onClick={() => handleSortClick(col.key)}
                  sx={{
                    width: colWidths[col.key],
                    minWidth: colWidths[col.key],
                    flexShrink: 0,
                    px: 1.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: col.align === 'center' ? 'center' : 'flex-start',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    position: 'relative',
                    boxSizing: 'border-box',
                    '&:hover': { backgroundColor: '#ececec' },
                  }}
                >
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, display: 'flex', alignItems: 'center' }}>
                    {col.label}
                    <SortIcon colKey={col.key} />
                  </Box>
                  {/* Resize handle */}
                  <Box
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 6,
                      cursor: 'col-resize',
                      zIndex: 1,
                      '&:hover': { backgroundColor: 'primary.light', opacity: 0.6 },
                      '&:active': { backgroundColor: 'primary.main', opacity: 0.8 },
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {filteredGrades.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                No hay datos para mostrar
              </Box>
            ) : (
              filteredGrades.slice(0, displayCount).map((grade, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    borderBottom: '1px solid #e0e0e0',
                    alignItems: 'flex-start',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  {visibleColumns.map((col) => {
                    const isComments = col.key === 'comments';
                    return (
                      <Box
                        key={col.key}
                        sx={{
                          width: colWidths[col.key],
                          minWidth: colWidths[col.key],
                          flexShrink: 0,
                          px: 1.5,
                          py: 1.25,
                          boxSizing: 'border-box',
                          textAlign: col.align === 'center' ? 'center' : 'left',
                          ...(isComments
                            ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: grade.comments ? 'text.primary' : 'text.disabled' }
                            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                        }}
                      >
                        {isComments ? (grade.comments || '-') : grade[col.key]}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}

            {/* Infinite scroll sentinel */}
            {filteredGrades.length > displayCount && (
              <Box ref={sentinelRef} sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* Column visibility popover */}
      <Popover
        open={Boolean(colMenuAnchor)}
        anchorEl={colMenuAnchor}
        onClose={() => setColMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">
            COLUMNAS VISIBLES
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1, py: 0.5, display: 'flex', flexDirection: 'column' }}>
          {COLUMNS.map((col) => (
            <FormControlLabel
              key={col.key}
              label={col.label}
              sx={{ mx: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={visibleCols[col.key]}
                  onChange={() => toggleCol(col.key)}
                />
              }
            />
          ))}
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button size="small" onClick={() => setVisibleCols(Object.fromEntries(COLUMNS.map((c) => [c.key, true])))}>
            Mostrar todas
          </Button>
        </Box>
      </Popover>
    </Container>
  );
};

export default RubricsGradesDetailsPage;
